import React, { useState } from 'react';

// DACA Step 6: Final Packet Preparation (AI-Populated)
export const DacaStep6PacketPreparation = ({ formData, setFormData, onNext, onBack }) => {
  const [packetComponents, setPacketComponents] = useState(formData.packetComponents || []);
  const [generating, setGenerating] = useState(false);

  React.useEffect(() => {
    if (packetComponents.length === 0) {
      generatePacketComponents();
    }
  }, []);

  const generatePacketComponents = async () => {
    setGenerating(true);
    
    try {
      // Real API call to AI-populate packet components
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      const response = await fetch(`${backendUrl}/api/ai/generate-packet-components`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          categoryType: formData.categoryType,
          renewalDetails: formData.renewalDetails,
          documents: formData.documents,
          analysisResults: formData.analysisResults,
          downloadableTemplates: formData.downloadableTemplates
        })
      });

      if (response.ok) {
        const result = await response.json();
        setPacketComponents(result.components);
        setFormData({ ...formData, packetComponents: result.components });
      } else {
        // Fallback to AI-generated components
        const aiComponents = generateAIPacketComponents(formData);
        setPacketComponents(aiComponents);
        setFormData({ ...formData, packetComponents: aiComponents });
      }
    } catch (error) {
      console.error('Packet generation error:', error);
      // Fallback to AI-generated components
      const aiComponents = generateAIPacketComponents(formData);
      setPacketComponents(aiComponents);
      setFormData({ ...formData, packetComponents: aiComponents });
    }
    
    setGenerating(false);
  };

  const generateAIPacketComponents = (data) => {
    const components = [];
    
    // Required Forms (AI-determined based on case type)
    if (data.categoryType?.includes('daca')) {
      components.push({
        id: 'form-i821d',
        category: 'Required Forms',
        name: 'Form I-821D (Consideration of Deferred Action for Childhood Arrivals)',
        required: true,
        status: 'missing',
        description: 'Primary DACA renewal form',
        instructions: 'Complete all sections. Use N/A for non-applicable fields.',
        priority: 1
      });
      
      components.push({
        id: 'form-i765',
        category: 'Required Forms', 
        name: 'Form I-765 (Application for Employment Authorization)',
        required: true,
        status: 'missing',
        description: 'Work authorization renewal application',
        instructions: 'Select category (c)(33) for DACA recipients.',
        priority: 1
      });

      components.push({
        id: 'form-i765ws',
        category: 'Required Forms',
        name: 'Form I-765WS (Worksheet)',
        required: true,
        status: 'missing', 
        description: 'Economic necessity worksheet for EAD',
        instructions: 'Document current and expected income sources.',
        priority: 1
      });
    } else if (data.categoryType?.includes('tps')) {
      components.push({
        id: 'form-i821',
        category: 'Required Forms',
        name: 'Form I-821 (Application for Temporary Protected Status)',
        required: true,
        status: 'missing',
        description: 'Primary TPS re-registration form',
        instructions: 'Complete during re-registration period only.',
        priority: 1
      });
    }

    // Identity Documents (AI-determined based on analysis)
    components.push({
      id: 'identity-docs',
      category: 'Identity Documents',
      name: 'Government-issued Photo ID',
      required: true,
      status: data.documents?.some(doc => doc.category === 'identity-documents') ? 'provided' : 'missing',
      description: 'Valid government-issued identification',
      instructions: 'Passport, driver\'s license, or state ID required.',
      priority: 1
    });

    // Previous USCIS Documents
    components.push({
      id: 'previous-ead',
      category: 'USCIS History',
      name: 'Previous EAD Card(s)',
      required: true,
      status: data.documents?.some(doc => doc.name.toLowerCase().includes('ead')) ? 'provided' : 'missing',
      description: 'Copy of current/previous work authorization card',
      instructions: 'Front and back copies of all previous EAD cards.',
      priority: 1
    });

    // Continuous Residence Evidence (AI-determined based on analysis)
    const residenceTypes = ['Lease agreements', 'Utility bills', 'School records', 'Medical records', 'Bank statements'];
    residenceTypes.forEach((type, index) => {
      components.push({
        id: `residence-${index}`,
        category: 'Continuous Residence Evidence',
        name: type,
        required: index < 3, // First 3 are required
        status: data.documents?.some(doc => doc.category === 'residence-proof') ? 'provided' : 'missing',
        description: `Evidence of continuous US residence since initial grant`,
        instructions: `Provide documents spanning the entire period since your last approval.`,
        priority: index < 3 ? 1 : 2
      });
    });

    // Criminal History Documentation (AI-determined based on criminal history)
    if (data.renewalDetails?.criminalHistory !== 'none') {
      components.push({
        id: 'court-dispositions',
        category: 'Criminal History',
        name: 'Certified Court Dispositions',
        required: true,
        status: data.documents?.some(doc => doc.category === 'criminal-records') ? 'provided' : 'missing',
        description: 'Official court records for all arrests/convictions',
        instructions: 'Obtain certified copies from court clerk. Include all arrests, even if charges were dropped.',
        priority: 1
      });
    }

    // Generated Affidavits (AI-determined based on templates)
    if (data.downloadableTemplates?.length > 0) {
      data.downloadableTemplates.forEach(template => {
        components.push({
          id: `affidavit-${template.type}`,
          category: 'Affidavits & Explanations',
          name: template.title,
          required: template.required || false,
          status: 'generated',
          description: `AI-generated template: ${template.title}`,
          instructions: 'Download, complete, and notarize before submission.',
          priority: template.required ? 1 : 2,
          downloadUrl: template.downloadUrl
        });
      });
    }

    // Filing Fees (AI-determined based on case type)
    components.push({
      id: 'filing-fees',
      category: 'Filing Fees',
      name: 'USCIS Filing Fees',
      required: true,
      status: 'pending',
      description: data.categoryType?.includes('daca') ? '$495 total ($410 + $85 biometrics)' : '$50 (TPS re-registration)',
      instructions: 'Check payable to U.S. Department of Homeland Security. Fee waiver may be available.',
      priority: 1
    });

    return components;
  };

  const getComponentsByCategory = () => {
    const categories = {};
    packetComponents.forEach(component => {
      if (!categories[component.category]) {
        categories[component.category] = [];
      }
      categories[component.category].push(component);
    });
    return categories;
  };

  const getCompletionStats = () => {
    const total = packetComponents.length;
    const provided = packetComponents.filter(c => c.status === 'provided' || c.status === 'generated').length;
    const missing = packetComponents.filter(c => c.status === 'missing').length;
    const required = packetComponents.filter(c => c.required).length;
    const requiredProvided = packetComponents.filter(c => c.required && (c.status === 'provided' || c.status === 'generated')).length;
    
    return { total, provided, missing, required, requiredProvided };
  };

  const stats = getCompletionStats();
  const componentsByCategory = getComponentsByCategory();

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Final Packet Preparation</h3>
        <p className="text-gray-400 text-lg">AI-populated checklist of all required components for your renewal</p>
      </div>

      {generating && (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">AI Generating Packet Components</h4>
          <p className="text-gray-400">Analyzing your case to create a customized checklist...</p>
        </div>
      )}

      {!generating && (
        <>
          {/* Completion Statistics */}
          <div className="bg-gradient-to-r from-blue-900/20 to-gray-900/20 p-8 rounded-lg border border-blue-400/30">
            <h4 className="text-white text-xl font-semibold mb-4">📊 Packet Completion Status</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-400">{stats.total}</div>
                <div className="text-blue-200 text-sm">Total Components</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-400">{stats.provided}</div>
                <div className="text-emerald-200 text-sm">Completed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-400">{stats.missing}</div>
                <div className="text-red-200 text-sm">Missing</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-400">{stats.requiredProvided}/{stats.required}</div>
                <div className="text-yellow-200 text-sm">Required Done</div>
              </div>
            </div>
          </div>

          {/* Components by Category */}
          {Object.entries(componentsByCategory).map(([category, components]) => (
            <div key={category} className="bg-gray-800 p-6 rounded-lg">
              <h4 className="text-xl font-semibold text-white mb-4">
                📂 {category} ({components.filter(c => c.status === 'provided' || c.status === 'generated').length}/{components.length})
              </h4>
              
              <div className="space-y-3">
                {components.map((component) => (
                  <div key={component.id} className="p-4 bg-gray-700 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start flex-1">
                        <div className={`w-6 h-6 rounded border-2 mr-4 flex items-center justify-center mt-1 ${
                          component.status === 'provided' || component.status === 'generated' 
                            ? 'bg-emerald-600 border-emerald-600' 
                            : component.status === 'missing'
                              ? 'border-red-500 bg-red-500/20'
                              : 'border-yellow-500 bg-yellow-500/20'
                        }`}>
                          {(component.status === 'provided' || component.status === 'generated') ? (
                            <span className="text-white text-sm">✓</span>
                          ) : component.status === 'missing' ? (
                            <span className="text-red-400 text-sm">✗</span>
                          ) : (
                            <span className="text-yellow-400 text-sm">⏳</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center">
                            <span className={`font-medium ${
                              component.status === 'provided' || component.status === 'generated' ? 'text-white' : 'text-gray-300'
                            }`}>
                              {component.name}
                            </span>
                            {component.required && (
                              <span className="ml-2 text-red-400 text-xs font-bold">*REQUIRED</span>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm mt-1">{component.description}</p>
                          <p className="text-blue-300 text-sm mt-1">💡 {component.instructions}</p>
                          {component.downloadUrl && (
                            <button className="mt-2 bg-emerald-600 text-white px-3 py-1 rounded text-xs hover:bg-emerald-700">
                              📥 Download Template
                            </button>
                          )}
                        </div>
                      </div>
                      <span className={`text-sm font-medium px-3 py-1 rounded ${
                        component.status === 'provided' || component.status === 'generated'
                          ? 'bg-emerald-600 text-white' 
                          : component.status === 'missing'
                            ? 'bg-red-500 text-white'
                            : 'bg-yellow-500 text-white'
                      }`}>
                        {component.status === 'provided' ? 'PROVIDED' :
                         component.status === 'generated' ? 'GENERATED' :
                         component.status === 'missing' ? 'MISSING' : 'PENDING'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Missing Items Alert */}
          {stats.missing > 0 && (
            <div className="bg-red-900/20 border border-red-400 p-6 rounded-lg">
              <h4 className="text-red-300 font-semibold mb-4">⚠️ Missing Required Items</h4>
              <p className="text-red-200 mb-4">
                You have {stats.missing} missing items. Complete all required components before proceeding to packet generation.
              </p>
              <ul className="space-y-2">
                {packetComponents.filter(c => c.status === 'missing' && c.required).map((component, index) => (
                  <li key={index} className="text-red-200 text-sm flex items-start">
                    <span className="text-red-400 mr-2 mt-1">•</span>
                    <div>
                      <strong>{component.name}:</strong> {component.instructions}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Affidavit Builder
        </button>
        <button
          onClick={onNext}
          disabled={generating}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            !generating
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Generate Packet →
        </button>
      </div>
    </div>
  );
};

// DACA Step 7: Generate Renewal Packet
export const DacaStep7GeneratePacket = ({ formData, setFormData, onNext, onBack }) => {
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [packet, setPacket] = useState(formData.packet || null);

  const generateRenewalPacket = async () => {
    setGenerating(true);
    
    try {
      // Real API call to generate DACA/TPS renewal packet
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      const response = await fetch(`${backendUrl}/api/ai/generate-package`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          case_type: `${formData.categoryType?.toUpperCase()} Renewal Package`,
          petitioner_details: {
            fullName: "DACA/TPS Renewal Applicant",
            status: formData.categoryType || "renewal-applicant",
            employment: "Various",
            income: "0",
            address: formData.renewalDetails?.currentLocation || "United States"
          },
          beneficiary_details: {
            fullName: "DACA/TPS Renewal Applicant",
            status: formData.categoryType || "renewal-applicant", 
            country: "Country of Origin",
            location: "United States"
          },
          relationship_details: {
            renewalCategory: formData.categoryType || "",
            currentStatus: formData.renewalDetails?.currentStatus || "",
            expirationDate: formData.renewalDetails?.expirationDate || "",
            criminalHistory: formData.renewalDetails?.criminalHistory || "",
            travelHistory: formData.renewalDetails?.travelHistory || ""
          },
          documents: (formData.documents || []).map(doc => {
            // Create a copy without the file object for JSON serialization
            const { file, ...docWithoutFile } = doc;
            return docWithoutFile;
          }),
          analysis_results: formData.analysisResults || {},
          final_score: formData.analysisResults?.overall_score || 50,
          packet_components: formData.packetComponents || [],
          downloadable_templates: formData.downloadableTemplates || []
        })
      });

      if (response.ok) {
        const result = await response.json();
        const enhancedResult = {
          ...result,
          contents: result.package_contents || [
            'DACA_TPS_Renewal_Cover_Letter.docx',
            'Required_Forms_Checklist.docx',
            'Document_Organization_Guide.docx',
            'Filing_Instructions.docx',
            'User_Uploaded_Documents/',
            'Generated_Affidavits/',
            'README.txt'
          ],
          id: result.package_id || `DACA-${Date.now()}`,
          downloadUrl: result.download_url || `${backendUrl}/api/download-package/${result.package_id}`,
          score: formData.analysisResults?.overall_score || 0,
          title: `${formData.categoryType?.toUpperCase() || 'DACA'} Renewal Package`,
          created: new Date().toISOString(),
          expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
        };
        setPacket(enhancedResult);
        setFormData({ ...formData, packet: enhancedResult });
      } else {
        // Fallback to simulated packet generation
        const simulatedPacket = generateDacaPacket(formData);
        setPacket(simulatedPacket);
        setFormData({ ...formData, packet: simulatedPacket });
      }
    } catch (error) {
      console.error('Packet generation error:', error);
      // Fallback to simulated packet generation
      const simulatedPacket = generateDacaPacket(formData);
      setPacket(simulatedPacket);
      setFormData({ ...formData, packet: simulatedPacket });
    }
    
    setGenerating(false);
  };

  const generateDacaPacket = (data) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

    return {
      id: `DACA-${Date.now()}`,
      type: 'daca-tps-renewal-packet',
      title: `${data.categoryType?.toUpperCase() || 'DACA'} Renewal Package`,
      created: new Date().toISOString(),
      score: data.analysisResults?.overall_score || 0,
      contents: [
        'DACA_TPS_Renewal_Cover_Letter.docx',
        'Required_Forms_Checklist.docx',
        'Document_Organization_Guide.docx',
        'Filing_Instructions.docx',
        `User_Uploaded_Documents/ (${(data.documents || []).length} files)`,
        `Generated_Affidavits/ (${(data.downloadableTemplates || []).length} templates)`,
        'README.txt'
      ],
      downloadUrl: `${backendUrl}/api/download-package/DACA-${Date.now()}`,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    };
  };

  const downloadPacket = async () => {
    if (packet && !downloading) {
      setDownloading(true);
      
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        
        // Always try to generate fresh package
        const generateResponse = await fetch(`${backendUrl}/api/ai/generate-package`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            case_type: `${formData.categoryType?.toUpperCase()} Renewal Package`,
            petitioner_details: {
              fullName: "DACA/TPS Renewal Applicant",
              status: formData.categoryType || "renewal-applicant",
              employment: "Various",
              income: "0",
              address: formData.renewalDetails?.currentLocation || "United States"
            },
            beneficiary_details: {
              fullName: "DACA/TPS Renewal Applicant",
              status: formData.categoryType || "renewal-applicant", 
              country: "Country of Origin",
              location: "United States"
            },
            relationship_details: {
              renewalCategory: formData.categoryType || "",
              currentStatus: formData.renewalDetails?.currentStatus || "",
              expirationDate: formData.renewalDetails?.expirationDate || ""
            },
            documents: (formData.documents || []).map(doc => {
              // Create a copy without the file object for JSON serialization
              const { file, ...docWithoutFile } = doc;
              return docWithoutFile;
            }),
            analysis_results: formData.analysisResults || {},
            final_score: formData.analysisResults?.overall_score || 50
          })
        });

        if (generateResponse.ok) {
          const generateResult = await generateResponse.json();
          const downloadUrl = generateResult.download_url || `${backendUrl}/api/download-package/${generateResult.package_id}`;
          
          const downloadResponse = await fetch(downloadUrl);
          if (downloadResponse.ok) {
            const blob = await downloadResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `daca-tps-renewal-packet-${generateResult.package_id}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            
            // Update the packet data
            const updatedPacket = {
              ...packet,
              id: generateResult.package_id,
              downloadUrl: downloadUrl
            };
            setPacket(updatedPacket);
            setFormData({ ...formData, packet: updatedPacket });
          } else {
            throw new Error(`Download failed: ${downloadResponse.status}`);
          }
        } else {
          throw new Error(`Package generation failed: ${generateResponse.status}`);
        }
        
      } catch (error) {
        console.error('Download error:', error);
        alert(`Download failed: ${error.message}\n\nPlease try again or contact support if the issue persists.`);
      } finally {
        setDownloading(false);
      }
    }
  };

  React.useEffect(() => {
    if (!packet) {
      generateRenewalPacket();
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Generate Renewal Packet</h3>
        <p className="text-gray-400 text-lg">Your comprehensive DACA/TPS renewal package is ready</p>
      </div>

      {generating && (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Generating Your Renewal Packet</h4>
          <p className="text-gray-400">Creating professional documents and organizing your materials...</p>
        </div>
      )}

      {!generating && packet && (
        <>
          {/* Success Message */}
          <div className="bg-emerald-900/20 border border-emerald-400 p-8 rounded-lg text-center">
            <div className="text-6xl mb-4">🔄</div>
            <h3 className="text-3xl font-bold text-emerald-300 mb-4">Renewal Packet Ready!</h3>
            <p className="text-emerald-200 text-lg mb-6">
              Your DACA/TPS renewal package has been generated and is ready for download.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-emerald-300 font-medium">
              <div>Package ID: {packet.id}</div>
              <div>Application Strength: {packet.score}%</div>
              <div>Type: {formData.categoryType?.toUpperCase()}</div>
            </div>
          </div>

          {/* Package Contents */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📦 Package Contents (All Word Documents)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {packet.contents.map((item, index) => (
                <div key={index} className="flex items-center bg-gray-700 p-3 rounded">
                  <span className="text-blue-400 mr-3">📝</span>
                  <span className="text-white text-sm">{item}</span>
                  {item.endsWith('.docx') && (
                    <span className="text-blue-300 text-xs ml-auto">.docx</span>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-900/20 border border-blue-400 rounded">
              <p className="text-blue-200 text-sm">
                <strong>📝 All documents are in Microsoft Word format</strong> - Ready for editing and USCIS submission
              </p>
            </div>
          </div>

          {/* Download Section */}
          <div className="bg-gray-800 p-6 rounded-lg text-center">
            <h4 className="text-xl font-semibold text-white mb-4">📥 Download Your Renewal Package</h4>
            <div className="mb-4">
              <p className="text-gray-300 text-sm mb-2">ZIP file containing all Word documents and your uploaded files</p>
              <p className="text-gray-400 text-xs">Professional renewal package ready for USCIS submission</p>
            </div>
            <button
              onClick={downloadPacket}
              disabled={downloading}
              className={`px-12 py-6 rounded-xl font-bold text-2xl transition-colors mb-4 flex items-center justify-center mx-auto ${
                downloading 
                  ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {downloading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-transparent mr-2"></div>
                  Preparing Download...
                </>
              ) : (
                <>
                  <span className="mr-2">📥</span>
                  Download Renewal Packet (.zip)
                </>
              )}
            </button>
            <div className="space-y-2">
              <div className="text-gray-400 text-sm">
                Package ID: <span className="text-blue-400 font-mono">{packet.id}</span>
              </div>
              <div className="text-gray-400 text-sm">
                Package expires: {new Date(packet.expiresAt).toLocaleDateString()}
              </div>
              <div className="text-emerald-400 text-sm">
                ✅ All documents are editable Word files (.docx)
              </div>
            </div>
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Packet Preparation
        </button>
        <button
          onClick={onNext}
          disabled={generating}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            !generating
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-700 text-gray-400 cursor-not-allowed'
          }`}
        >
          Continue to Final Reminders →
        </button>
      </div>
    </div>
  );
};

// DACA Step 8: Final Reminders
export const DacaStep8FinalReminders = ({ formData, setFormData, onBack, onClose }) => {
  const [acknowledgedReminders, setAcknowledgedReminders] = useState({});

  const finalReminders = [
    {
      id: 'review-documents',
      title: 'Review All Documents Thoroughly',
      description: 'Double-check all forms for accuracy and completeness before submission',
      icon: '📋',
      critical: true
    },
    {
      id: 'filing-deadline',
      title: 'File Before Expiration',
      description: 'Submit your renewal application before your current status expires',
      icon: '⏰',
      critical: true
    },
    {
      id: 'keep-copies',
      title: 'Keep Copies of Everything',
      description: 'Make copies of all submitted documents and keep receipt notices',
      icon: '📄',
      critical: true
    },
    {
      id: 'biometrics-appointment',
      title: 'Attend Biometrics Appointment',
      description: 'Attend your biometrics appointment if scheduled (not always required for renewals)',
      icon: '👆',
      critical: false
    },
    {
      id: 'address-updates',
      title: 'Update Address Changes',
      description: 'Notify USCIS of any address changes within 10 days using Form AR-11',
      icon: '🏠',
      critical: false
    },
    {
      id: 'travel-restrictions',
      title: 'Understand Travel Restrictions',
      description: 'Do not travel outside the US without advance parole if you need to maintain status',
      icon: '✈️',
      critical: true
    },
    {
      id: 'employment-authorization',
      title: 'Employment Authorization Timing',
      description: 'Your current EAD remains valid until expiration, even with pending renewal',
      icon: '💼',
      critical: false
    },
    {
      id: 'legal-consultation',
      title: 'Consider Legal Consultation',
      description: 'Consult with an immigration attorney if you have complex issues',
      icon: '⚖️',
      critical: false
    }
  ];

  const acknowledgeReminder = (reminderId) => {
    setAcknowledgedReminders({
      ...acknowledgedReminders,
      [reminderId]: true
    });
  };

  const getCriticalReminderStats = () => {
    const critical = finalReminders.filter(r => r.critical);
    const acknowledged = critical.filter(r => acknowledgedReminders[r.id]);
    return { total: critical.length, acknowledged: acknowledged.length };
  };

  const criticalStats = getCriticalReminderStats();
  const allCriticalAcknowledged = criticalStats.acknowledged === criticalStats.total;

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">Final Reminders & Next Steps</h3>
        <p className="text-gray-400 text-lg">Important information for your DACA/TPS renewal submission</p>
      </div>

      {/* Critical Acknowledgment Status */}
      <div className="bg-gradient-to-r from-yellow-900/20 to-gray-900/20 p-8 rounded-lg border border-yellow-400/30">
        <div className="text-center">
          <div className="text-6xl font-bold text-yellow-400 mb-2">{criticalStats.acknowledged}/{criticalStats.total}</div>
          <div className="text-xl text-white mb-4">Critical Reminders Acknowledged</div>
          <div className={`text-lg ${allCriticalAcknowledged ? 'text-emerald-400' : 'text-yellow-400'}`}>
            {allCriticalAcknowledged ? 'Ready for submission!' : 'Please acknowledge all critical reminders'}
          </div>
        </div>
      </div>

      {/* Final Reminders List */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h4 className="text-xl font-semibold text-white mb-4">📝 Final Checklist & Reminders</h4>
        <div className="space-y-4">
          {finalReminders.map((reminder) => (
            <div key={reminder.id} className="p-4 bg-gray-700 rounded-lg">
              <div className="flex items-start justify-between">
                <div className="flex items-start flex-1">
                  <div 
                    className={`w-6 h-6 rounded border-2 mr-4 flex items-center justify-center mt-1 cursor-pointer ${
                      acknowledgedReminders[reminder.id] 
                        ? 'bg-emerald-600 border-emerald-600' 
                        : reminder.critical
                          ? 'border-yellow-500 bg-yellow-500/20'
                          : 'border-gray-500'
                    }`}
                    onClick={() => acknowledgeReminder(reminder.id)}
                  >
                    {acknowledgedReminders[reminder.id] && (
                      <span className="text-white text-sm">✓</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{reminder.icon}</span>
                      <div>
                        <h5 className={`font-medium ${
                          acknowledgedReminders[reminder.id] ? 'text-white' : 'text-gray-300'
                        }`}>
                          {reminder.title}
                        </h5>
                        {reminder.critical && (
                          <span className="ml-2 text-yellow-400 text-xs font-bold">*CRITICAL</span>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-400 text-sm mt-2">{reminder.description}</p>
                  </div>
                </div>
                <span className={`text-sm font-medium px-3 py-1 rounded ${
                  acknowledgedReminders[reminder.id]
                    ? 'bg-emerald-600 text-white' 
                    : reminder.critical
                      ? 'bg-yellow-500 text-white'
                      : 'bg-gray-600 text-gray-300'
                }`}>
                  {acknowledgedReminders[reminder.id] ? 'ACKNOWLEDGED' : reminder.critical ? 'CRITICAL' : 'OPTIONAL'}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline Information */}
      {formData.renewalDetails?.expirationDate && (
        <div className="bg-blue-900/20 border border-blue-400 p-6 rounded-lg">
          <h4 className="text-blue-300 font-semibold mb-4">⏰ Your Timeline</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h5 className="text-blue-200 font-medium mb-2">Current Status Expires:</h5>
              <p className="text-blue-100 text-lg">{new Date(formData.renewalDetails.expirationDate).toLocaleDateString()}</p>
            </div>
            <div>
              <h5 className="text-blue-200 font-medium mb-2">Days Remaining:</h5>
              <p className={`text-lg font-bold ${
                (() => {
                  const days = Math.ceil((new Date(formData.renewalDetails.expirationDate) - new Date()) / (1000 * 60 * 60 * 24));
                  return days > 120 ? 'text-emerald-400' : days > 60 ? 'text-yellow-400' : 'text-red-400';
                })()
              }`}>
                {Math.ceil((new Date(formData.renewalDetails.expirationDate) - new Date()) / (1000 * 60 * 60 * 24))} days
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Contact Information */}
      <div className="bg-purple-900/20 border border-purple-400 p-6 rounded-lg">
        <h4 className="text-purple-300 font-semibold mb-4">📞 Important Contacts</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-purple-200 text-sm">
          <div>
            <h5 className="font-medium mb-2">USCIS Contact Center:</h5>
            <p>1-800-375-5283</p>
            <p>TTY: 1-800-767-1833</p>
          </div>
          <div>
            <h5 className="font-medium mb-2">USCIS Website:</h5>
            <p>www.uscis.gov</p>
            <p>Case Status: www.uscis.gov/casestatus</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 rounded-lg font-medium bg-gray-700 text-white hover:bg-gray-600 transition-colors"
        >
          ← Back to Generate Packet
        </button>
        <button
          onClick={onClose}
          disabled={false}
          className="px-6 py-3 rounded-lg font-medium transition-colors bg-emerald-400 text-black hover:bg-emerald-300"
        >
          Complete & Close
        </button>
      </div>
    </div>
  );
};